import React, { useRef, useState, useEffect, useCallback } from "react";
import { FaCloudUploadAlt, FaTimes } from "react-icons/fa";
import Cropper from "react-easy-crop";
import axios from "axios";
import secureLocalStorage from "react-secure-storage";
import { useUserContext } from "../context/UserContext";

const ImageUpload = ({ fetchUser, currentPhoto }) => {

    const { getAuth } = useUserContext();

    const [file, setFile] = useState(null);
    const [isGif, setIsGif] = useState(false);
    const [isVideo, setIsVideo] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const fileInputRef = useRef(null);
    const storedUser = JSON.parse(secureLocalStorage.getItem("auth_data")) || {};
    const userId = storedUser?.id || "";
    const [message, setMessage] = useState(null);
    const [messageType, setMessageType] = useState(null);

    const BASE_URL = "https://buzzinguniverse.com/backend/";
    const hasPhoto = currentPhoto && currentPhoto !== BASE_URL;

    const handleRemovePhoto = async (e) => {
        e.target.classList.add("loadin");
        try {
            const formData = new FormData();
            formData.append("id", userId);
            formData.append("remove_photo", "1");
            const response = await axios.post(
                "https://buzzinguniverse.com/backend/api/update-user",
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            if (response.status === 200) {
                getAuth();
                fetchUser();
                setMessage("Profile picture removed successfully!");
                setMessageType("success");
            } else {
                throw new Error("Unexpected response");
            }
        } catch (error) {
            setMessage("Failed to remove profile picture.");
            setMessageType("error");
        }
        e.target.classList.remove("loadin");
    };

    useEffect(() => {
        return () => {
            if (previewImage) {
                URL.revokeObjectURL(previewImage);
            }
        };
    }, []);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            const gif = selectedFile.type === 'image/gif';
            const video = selectedFile.type.startsWith('video/');
            setFile(selectedFile);
            setIsGif(gif);
            setIsVideo(video);
            setPreviewImage(URL.createObjectURL(selectedFile));
            setCroppedImage(null);
            setMessage(null);
        }
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const generateCroppedImage = async () => {
        if (!previewImage || !croppedAreaPixels) return;
        const image = new Image();
        image.src = previewImage;
        await image.decode();
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        canvas.width = croppedAreaPixels.width;
        canvas.height = croppedAreaPixels.height;
        ctx.drawImage(
            image,
            croppedAreaPixels.x,
            croppedAreaPixels.y,
            croppedAreaPixels.width,
            croppedAreaPixels.height,
            0,
            0,
            croppedAreaPixels.width,
            croppedAreaPixels.height
        );
        canvas.toBlob((blob) => setCroppedImage(blob), "image/jpeg");
    };

    const handleUpload = async (e) => {
        e.target.classList.add("loadin");

        if (!userId) {
            setMessage("User ID not found. Please log in again.");
            setMessageType("error");
            e.target.classList.remove("loadin");
            return;
        }

        // GIF/Video: upload original file directly (no canvas processing)
        const directUpload = isGif || isVideo;
        const ext = isVideo ? (file.name.split('.').pop() || 'mp4') : 'gif';
        const uploadBlob = directUpload ? file : croppedImage;
        const fileName = directUpload ? `profile.${ext}` : "profile.jpg";

        if (!uploadBlob) {
            setMessage(directUpload ? "Please select a file first!" : "Please crop and confirm the image first!");
            setMessageType("error");
            e.target.classList.remove("loadin");
            return;
        }

        const formData = new FormData();
        formData.append("id", userId);
        formData.append("photo", uploadBlob, fileName);
        try {
            const response = await axios.post(
                "https://buzzinguniverse.com/backend/api/update-user",
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            if (response.status === 200) {
                getAuth();
                fetchUser();
                setMessage("Image uploaded successfully!");
                setMessageType("success");
            } else {
                throw new Error("Unexpected API response");
            }
        } catch (error) {
            console.error("Upload failed:", error);
            setMessage("Failed to upload image.");
            setMessageType("error");
        }
        e.target.classList.remove("loadin");
    };

    const handleReset = () => {
        setFile(null);
        setIsGif(false);
        setIsVideo(false);
        setPreviewImage(null);
        setCroppedImage(null);
        setMessage(null);
    };

    return (
        <div className="image-upload-container">
            {hasPhoto && !previewImage && (
                <div className="current-photo-preview text-center mb-3">
                    <img src={currentPhoto} alt="Current Profile" style={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover', border: '3px solid #ddd' }} />
                    <div className="mt-2">
                        <button onClick={handleRemovePhoto} style={{ background: '#dc3545', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 16px', cursor: 'pointer' }}>
                            <FaTimes style={{ marginRight: 5 }} /> Remove Profile Picture
                        </button>
                    </div>
                    {message && !previewImage && (
                        <p className={`message ${messageType === "success" ? "success-message" : "error-message"}`}>{message}</p>
                    )}
                </div>
            )}
            {!previewImage && (
                <div className="file-upload-wrapper">
                    <label className="file-upload-box mb-0">
                        <input
                            type="file"
                            accept=".png, .jpeg, .jpg, .webp, .gif, .mp4, .webm, .mov"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            className="hidden"
                            hidden
                        />
                        <div className="upload-content">
                            <FaCloudUploadAlt className="upload-icon" />
                            <h5 className="mb-2">Upload your profile picture</h5>
                            <p className="mb-0" style={{ fontSize: 12, color: '#888' }}>PNG, JPG, WEBP, GIF or Video (MP4, WEBM, MOV)</p>
                        </div>
                    </label>
                </div>
            )}

            {/* GIF / Video flow: direct upload, no cropper */}
            {previewImage && (isGif || isVideo) && (
                <div className="profile-image-preview mt-4 text-center">
                    {isVideo ? (
                        <video
                            src={previewImage}
                            autoPlay loop muted playsInline
                            style={{ width: 150, height: 150, borderRadius: '50%', objectFit: 'cover', border: '3px solid #ddd' }}
                        />
                    ) : (
                        <img src={previewImage} alt="GIF Preview" style={{ width: 150, height: 150, borderRadius: '50%', objectFit: 'cover', border: '3px solid #ddd' }} />
                    )}
                    <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                        {isVideo ? 'Video will loop on your profile' : 'GIF animation will be preserved'}
                    </p>
                    <button onClick={handleUpload}>Set Profile Picture</button>
                    <button onClick={handleReset}>
                        <FaTimes /> Remove
                    </button>
                    {message && (
                        <p className={`message ${messageType === "success" ? "success-message" : "error-message"}`}>{message}</p>
                    )}
                </div>
            )}

            {/* Non-GIF flow: show cropper */}
            {previewImage && !isGif && !croppedImage && (
                <div className="cropper-container">
                    <div className="cropper-wrapper">
                        <Cropper
                            image={previewImage}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                        />
                    </div>
                    <button onClick={generateCroppedImage}>Crop Image</button>
                    <button onClick={handleReset}>
                        <FaTimes /> Remove
                    </button>
                </div>
            )}
            {croppedImage && !isGif && (
                <div className="profile-image-preview mt-4 text-center">
                    <img src={URL.createObjectURL(croppedImage)} alt="Cropped Preview" />
                    <button onClick={handleUpload}>Set Profile Picture</button>
                    <button onClick={handleReset}>
                        <FaTimes /> Remove
                    </button>
                    {message && (
                        <p className={`message ${messageType === "success" ? "success-message" : "error-message"}`}>{message}</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default ImageUpload;
